import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../hooks/AuthContext';
import { useControllerFlowStepsContext } from '../../../hooks/ControllerFlowStepsContext';
import Button from '../../../components/Button/Button';
import ButtonLabel from '../../../components/Button/ButtonLabel';
import CheckForcePassword from '../../../components/CheckForcePassword/CheckForcePassword';
import Icon from '../../../components/Icon/Icon';
import {
  PROVIDER_SOCIAL,
  PASS_MAX_LENGTH,
  LINK_PRIVACY_POLICY
} from '../../../data/Constants';
import {
  checkUserHasPassword,
  createPassword,
  logoutSSO,
  updatePassword,
  validateUserIdentity
} from '../../../services/user';
import {
  updateUsersActive,
  unlinkSocialAccount
} from '../../../services/accountController';
import { passwordMask } from '../../../utils/Masks';
import { SocialActionEnum } from '../../../hooks/ControllerSocialMedia';
import ButtonGoogle from '../../../components/SocialMedia/ButtonGoogle';
import ButtonFace from '../../../components/SocialMedia/ButtonFace';
import ButtonApple from '../../../components/SocialMedia/ButtonApple';
import ButtonMicrosoft from '../../../components/SocialMedia/ButtonMicrosoft';

type SecuritySectionId = 'senha' | 'contas-sociais' | 'excluir-conta';

interface SecuritySubLink {
  title: string;
  id: SecuritySectionId;
}

interface SocialProviderConfig {
  id: string;
  provider: PROVIDER_SOCIAL;
  label: string;
  userField: 'faceId' | 'googleId' | 'appleId' | 'microsoftId';
}

const SUBLINKS = [
  { title: 'Senha', id: 'senha' },
  { title: 'Contas sociais', id: 'contas-sociais' },
  { title: 'Excluir conta', id: 'excluir-conta' }
] as SecuritySubLink[];

const SOCIAL_PROVIDERS: SocialProviderConfig[] = [
  {
    id: 'facebook',
    provider: PROVIDER_SOCIAL.FACEBOOK,
    label: 'Facebook',
    userField: 'faceId'
  },
  {
    id: 'google',
    provider: PROVIDER_SOCIAL.GOOGLE,
    label: 'Google',
    userField: 'googleId'
  },
  {
    id: 'apple',
    provider: PROVIDER_SOCIAL.APPLE,
    label: 'Apple',
    userField: 'appleId'
  },
  {
    id: 'microsoft',
    provider: PROVIDER_SOCIAL.MICROSOFT,
    label: 'Microsoft',
    userField: 'microsoftId'
  }
];

function ConnectSocialButton({ provider }: { provider: PROVIDER_SOCIAL }) {
  if (provider === PROVIDER_SOCIAL.GOOGLE)
    return <ButtonGoogle action={SocialActionEnum.CONNECT_ACCOUNT} showText />;

  if (provider === PROVIDER_SOCIAL.FACEBOOK)
    return <ButtonFace action={SocialActionEnum.CONNECT_ACCOUNT} showText />;

  if (provider === PROVIDER_SOCIAL.APPLE)
    return <ButtonApple action={SocialActionEnum.CONNECT_ACCOUNT} showText />;

  return <ButtonMicrosoft action={SocialActionEnum.CONNECT_ACCOUNT} showText />;
}

function SenhaPanel({
  hasPassword,
  setHasPassword
}: {
  hasPassword: boolean;
  setHasPassword: (value: boolean) => void;
}) {
  const { checkAuth } = useAuthContext();
  const { setAlertBox, showErrorMessageServer } =
    useControllerFlowStepsContext();
  const [loading, setLoading] = useState(false);
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const {
    register,
    getValues,
    watch,
    setError,
    trigger,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm<any>({ mode: 'onChange' });

  const watchedValues = watch(['currentPassword', 'password', 'checkPassword']);
  const [currentPwd, newPwd, confirmPwd] = watchedValues;

  function checkPasswordsMatch() {
    if (getValues('password') === getValues('checkPassword')) {
      clearErrors('checkPassword');
    } else {
      setError('checkPassword', { message: 'as senhas não são iguais' });
    }
  }

  async function handleCreatePassword() {
    const valid = await trigger(['password', 'checkPassword']);
    if (!valid || !checkAuth().id) return;

    setLoading(true);
    try {
      await createPassword(getValues('password'));
      setValue('password', '');
      setValue('checkPassword', '');
      setHasPassword(true);
      setAlertBox({ message: 'sua senha foi criada', error: false });
    } catch {
      showErrorMessageServer();
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    const valid = await trigger([
      'currentPassword',
      'password',
      'checkPassword'
    ]);
    if (!valid || !checkAuth().id) return;

    setLoading(true);
    try {
      const response = await validateUserIdentity({
        email: checkAuth().email,
        password: getValues('currentPassword')
      });
      if (!response.indentityValidated) {
        setError('currentPassword', { message: 'senha incorreta' });
        setLoading(false);
        return;
      }
      await updatePassword(getValues('currentPassword'), getValues('password'));
      setValue('currentPassword', '');
      setValue('password', '');
      setValue('checkPassword', '');
      setAlertBox({ message: 'sua senha foi atualizada', error: false });
    } catch {
      showErrorMessageServer();
    } finally {
      setLoading(false);
    }
  }

  const canSubmit =
    newPwd?.length >= 6 &&
    confirmPwd?.length >= 6 &&
    !errors.password &&
    !errors.checkPassword;

  const canUpdatePassword =
    canSubmit && currentPwd?.length >= 6 && !errors.currentPassword;

  return (
    <div className="up-security-panel">
      <div className="up-security-panel__field-group">
        {!hasPassword && (
          <span className="md color-text-default">
            Sua conta ainda não possui senha. Crie uma senha para acessar sem
            login social.
          </span>
        )}
        {hasPassword && (
          <div className="form-container">
            <label className="form-field-label">senha atual</label>
            <div className="form-field">
              <input
                className="caption"
                type={showCurrentPass ? 'text' : 'password'}
                placeholder="digite sua senha atual"
                maxLength={Number(PASS_MAX_LENGTH)}
                {...register('currentPassword')}
                onInput={passwordMask}
              />
              <div
                className="form-field-sufix"
                onClick={() => setShowCurrentPass((p) => !p)}
              >
                <Icon name={showCurrentPass ? 'eyeClose' : 'eye'} />
              </div>
            </div>
            {errors.currentPassword && (
              <p className="ctv-error-message mt-8">
                {errors.currentPassword.message as string}
              </p>
            )}
          </div>
        )}
        <CheckForcePassword value={newPwd || ''} errorField={!errors.password}>
          <div className="form-container mt-8">
            <label className="form-field-label">
              {hasPassword ? 'nova senha' : 'criar senha'}
            </label>
            <div className="form-field">
              <input
                className="caption"
                type={showNewPass ? 'text' : 'password'}
                placeholder={
                  hasPassword ? 'digite sua nova senha' : 'digite sua senha'
                }
                maxLength={Number(PASS_MAX_LENGTH)}
                {...register('password')}
                onInput={passwordMask}
                onBlur={checkPasswordsMatch}
              />
              <div
                className="form-field-sufix"
                onClick={() => setShowNewPass((p) => !p)}
              >
                <Icon name={showNewPass ? 'eyeClose' : 'eye'} />
              </div>
            </div>
            {errors.password && (
              <p className="ctv-error-message mt-8">
                {errors.password.message as string}
              </p>
            )}
          </div>
        </CheckForcePassword>
        <div className="form-container mt-8">
          <label className="form-field-label">confirme a nova senha</label>
          <div className="form-field">
            <input
              className="caption"
              type={showConfirmPass ? 'text' : 'password'}
              placeholder="repita sua nova senha"
              maxLength={Number(PASS_MAX_LENGTH)}
              {...register('checkPassword')}
              onInput={passwordMask}
            />
            <div
              className="form-field-sufix"
              onClick={() => setShowConfirmPass((p) => !p)}
            >
              <Icon name={showConfirmPass ? 'eyeClose' : 'eye'} />
            </div>
          </div>
          {errors.checkPassword && (
            <p className="ctv-error-message mt-8">
              {errors.checkPassword.message as string}
            </p>
          )}
        </div>
      </div>
      <Button
        id="up-update-password"
        className="mt-24 mbi-0"
        onClick={hasPassword ? handleSubmit : handleCreatePassword}
        loading={loading}
        disabled={
          hasPassword ? !canUpdatePassword || loading : !canSubmit || loading
        }
      >
        <ButtonLabel loading={loading}>
          {hasPassword ? 'alterar senha' : 'criar senha'}
        </ButtonLabel>
      </Button>
    </div>
  );
}

function SocialAccountsPanel({
  hasPassword,
  onNeedPassword
}: {
  hasPassword: boolean;
  onNeedPassword: () => void;
}) {
  const { user, setUser } = useAuthContext();
  const { setAlertBox, showErrorMessageServer } =
    useControllerFlowStepsContext();
  const [loadingProvider, setLoadingProvider] = useState('');

  const connectedCount = SOCIAL_PROVIDERS.filter(
    (item) => !!user?.[item.userField]
  ).length;

  async function disconnectProvider(item: SocialProviderConfig) {
    if (!hasPassword && connectedCount <= 1) {
      setAlertBox({
        message:
          'Crie uma senha antes de desconectar sua única conta social vinculada.',
        error: true
      });
      onNeedPassword();
      return;
    }

    setLoadingProvider(item.provider);
    try {
      await unlinkSocialAccount(item.provider);
      if (user) {
        setUser({ ...user, [item.userField]: '' } as any);
      }
      setAlertBox({
        message: `conta do ${item.label} desconectada com sucesso`,
        error: false
      });
    } catch {
      showErrorMessageServer();
    } finally {
      setLoadingProvider('');
    }
  }

  return (
    <div className="up-security-panel">
      <p className="up-security-panel__info">
        Conecte ou desconecte contas sociais para entrar na sua Conta SBT.
      </p>

      <div className="user-profile__social-list">
        {SOCIAL_PROVIDERS.map((item) => {
          const connected = !!user?.[item.userField];

          return (
            <div className="user-profile__social-item" key={item.id}>
              <div className="user-profile__social-item-data">
                <span className="md-bold color-text-default">{item.label}</span>
                <span className="sm color-text-default">
                  {connected ? 'conectada' : 'não conectada'}
                </span>
              </div>

              {connected ? (
                <Button
                  customColors="btn-white"
                  className="mbi-0"
                  loading={loadingProvider === item.provider}
                  disabled={loadingProvider === item.provider}
                  onClick={() => disconnectProvider(item)}
                >
                  <ButtonLabel>desconectar</ButtonLabel>
                </Button>
              ) : (
                <div className="user-profile__social-connect-btn">
                  <ConnectSocialButton provider={item.provider} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExcluirContaPanel({ hasPassword }: { hasPassword: boolean }) {
  const navigate = useNavigate();
  const { user, clearSession, setAccountDisabled } = useAuthContext();
  const { showErrorMessageServer } = useControllerFlowStepsContext();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [accountDisabledSuccess, setAccountDisabledSuccess] = useState(false);
  const [showDisablePass, setShowDisablePass] = useState(false);

  async function handleDisableAccount() {
    setLoading(true);
    setPasswordError('');
    try {
      if (hasPassword) {
        const response = await validateUserIdentity({
          email: user?.email || '',
          password
        });

        if (!response.indentityValidated) {
          setPasswordError('senha incorreta');
          setLoading(false);
          return;
        }
      }

      await updateUsersActive(false);
      setAccountDisabled(true);
      setAccountDisabledSuccess(true);
    } catch {
      showErrorMessageServer();
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await logoutSSO();
    } catch (error) {
      console.error(error);
    } finally {
      clearSession();
      navigate('/', { replace: true });
    }
  }

  if (accountDisabledSuccess) {
    return (
      <div className="up-security-panel">
        <div className="up-security-panel__field-group">
          <h2 className="color-text-default">sua Conta SBT foi excluída</h2>
          <span className="md color-text-default">
            Você tem 30 dias para reativar sua conta realizando login novamente.
          </span>
          <Button className="mt-16 mbi-0" onClick={handleLogout}>
            <ButtonLabel>sair</ButtonLabel>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="up-security-panel">
      <div className="up-security-panel__field-group">
        <h2 className="color-text-default">excluir conta</h2>
        <span className="md color-text-default">
          Você está iniciando o processo de exclusão da sua Conta SBT. Os dados
          vinculados à sua conta deixarão de ficar disponíveis após o período de
          reativação.
        </span>
        <span className="md color-text-default">
          Caso mude de ideia, você tem 30 dias para reativar a conta fazendo
          login novamente.
        </span>
        <span className="md color-text-default">
          Para mais detalhes sobre tratamento de dados, consulte a{' '}
          <a href={LINK_PRIVACY_POLICY} target="_blank" rel="noreferrer">
            política de privacidade
          </a>
          .
        </span>

        {hasPassword && (
          <>
            <div className="form-container mt-16">
              <label className="form-field-label">senha</label>
              <div className="form-field">
                <input
                  className="caption"
                  type={showDisablePass ? 'text' : 'password'}
                  placeholder="digite sua senha"
                  maxLength={Number(PASS_MAX_LENGTH)}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  onInput={passwordMask}
                />
                <div
                  className="form-field-sufix"
                  onClick={() => setShowDisablePass((p) => !p)}
                >
                  <Icon name={showDisablePass ? 'eyeClose' : 'eye'} />
                </div>
              </div>
              {!!passwordError && (
                <p className="ctv-error-message mt-8">{passwordError}</p>
              )}
            </div>
          </>
        )}

        <Button
          className="mt-16 mbi-0"
          customColors="btn-white"
          loading={loading}
          disabled={loading || (hasPassword && password.length < 6)}
          onClick={handleDisableAccount}
        >
          <ButtonLabel loading={loading}>excluir conta</ButtonLabel>
        </Button>
      </div>
    </div>
  );
}

export default function SegurancaTab() {
  const { user } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(0);
  const [hasPassword, setHasPassword] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { showErrorMessageServer } = useControllerFlowStepsContext();

  useEffect(() => {
    const currentSection = searchParams.get('secao');
    const sectionIndex = SUBLINKS.findIndex(
      (link) => link.id === currentSection
    );
    setSelected(sectionIndex >= 0 ? sectionIndex : 0);
  }, [searchParams]);

  useEffect(() => {
    async function loadHasPassword() {
      if (!user?.email) return;

      try {
        const response = await checkUserHasPassword(user.email);
        setHasPassword(response.hasPassword);
      } catch {
        showErrorMessageServer();
      }
    }

    loadHasPassword();
  }, [user?.email]);

  function selectSection(index: number) {
    setSelected(index);
    setIsMenuOpen(false);

    const params = new URLSearchParams(searchParams);
    const selectedSection = SUBLINKS[index]?.id;

    if (!selectedSection || selectedSection === 'senha') {
      params.delete('secao');
    } else {
      params.set('secao', selectedSection);
    }

    setSearchParams(params, { replace: true });
  }

  function toggleMenu() {
    setIsMenuOpen((prev) => !prev);
  }

  function selectSenhaSection() {
    const senhaIndex = SUBLINKS.findIndex((link) => link.id === 'senha');
    if (senhaIndex >= 0) {
      selectSection(senhaIndex);
    }
  }

  const currentSubLink = SUBLINKS[selected];

  return (
    <div className="user-profile__content">
      <div className="user-profile__left-rail">
        <div
          className={`user-profile__left-rail-header${isMenuOpen ? ' user-profile__left-rail-header--open' : ''}`}
          onClick={toggleMenu}
        >
          <span>Segurança</span>
        </div>
        <div className="user-profile__left-rail-divider" />
        <div
          className={`user-profile__left-rail-nav${isMenuOpen ? ' user-profile__left-rail-nav--open' : ''}`}
        >
          {SUBLINKS.map((link, index) => (
            <button
              key={link.id}
              className={`user-profile__nav-item${selected === index ? ' user-profile__nav-item--active' : ''}`}
              onClick={() => selectSection(index)}
            >
              {link.title}
            </button>
          ))}
        </div>
      </div>

      <div className="user-profile__main">
        <div className="user-profile__main-header">
          <span className="user-profile__main-title">
            {currentSubLink.title}
          </span>
        </div>
        <div className="user-profile__main-divider" />
        {currentSubLink.id === 'senha' && (
          <SenhaPanel
            hasPassword={hasPassword}
            setHasPassword={setHasPassword}
          />
        )}
        {currentSubLink.id === 'contas-sociais' && (
          <SocialAccountsPanel
            hasPassword={hasPassword}
            onNeedPassword={selectSenhaSection}
          />
        )}
        {currentSubLink.id === 'excluir-conta' && (
          <ExcluirContaPanel hasPassword={hasPassword} />
        )}
      </div>
    </div>
  );
}
